import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Keyboard,
  ViewportFit,
  Tabs,
  TabItem,
  ButtonGroup,
  Slider,
  FormSection,
  Section,
  Palette,
  Switch,
  Button,
  DecorativeLight,
  mapLightingOptionsWithIcons,
  LIGHTING_MODE_BUTTON_GROUP_CLASS,
  isXingshanDk68FamilyStripKeyboardEquipment,
  isXingshanRealtimeStripKeyboardEquipment,
} from "@eweadn-qiankun/shared-components";
import type {
  CommunityPreviewDeviceMeta,
  FullBoardConfigData,
} from "@eweadn-qiankun/shared-types";
import {
  buildPreviewKeyboardLayoutWithChangeKey,
  resolveCommunityPreviewKeyboardType,
} from "../utils/buildPreviewKeyboardLayout";
import { getLightingPreviewParams } from "../utils/boardConfigPreviewUtils";
import {
  buildHuafendaStripPreviewFromBase,
  buildRongyuanStripPreviewFromBase,
  getLightingModeOptions,
  getPreviewSliderRange,
  resolveCommunityStripPreviewCols,
  resolveKeyboardLightingTabs,
  resolvePreviewSliderValue,
  sliceCommunityStripLightingData,
  shouldHideHuafendaStripPalette,
  shouldHideHuafendaStripSpeed,
  shouldShowLightingDirection,
  shouldShowXingshanStaticColor,
  STATIC_EFFECT_PALETTE_COLORS,
} from "../utils/lightingPreviewUtils";
import {
  shouldHideHuafendaClassicX87LightStripSpeedAndBrightness,
  shouldShowHuafendaLightStripVisualPreview,
  shouldShowRongyuanLightStripVisualPreview,
} from "@eweadn-qiankun/shared-utils";
import {
  shouldUseXingshanStripRoutinePreview,
  useXingshanStripRoutinePreview,
} from "../../../../../keyboard-management/src/utils/xingshanStripRoutinePreview";
import "../../../../../keyboard-management/src/pages/lighting/index.scss";

function rgbToHex({ R, G, B }: { R: number; G: number; B: number }): string {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(R)}${toHex(G)}${toHex(B)}`;
}

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: FullBoardConfigData;
  /** 0=Win，1=Mac；Base 层改键与子应用 useKeyboardLayout(layer:0) 对齐 */
  currentDeviceSystem?: number;
}

export default function KeyboardLightingSection({
  meta,
  configData,
  currentDeviceSystem = 0,
}: Props) {
  const { t } = useTranslation();
  const lightingTabs = useMemo(() => resolveKeyboardLightingTabs(meta), [meta]);
  const [tab, setTab] = useState("backlight");

  useEffect(() => {
    if (!lightingTabs.includes(tab as "backlight" | "strip")) {
      setTab("backlight");
    }
  }, [lightingTabs, tab]);

  const previewKeyboardType = useMemo(
    () => resolveCommunityPreviewKeyboardType(meta),
    [meta]
  );
  const layout = useMemo(
    () =>
      buildPreviewKeyboardLayoutWithChangeKey(
        meta,
        configData,
        currentDeviceSystem
      ),
    [meta, configData, currentDeviceSystem]
  );
  const isHfd = meta.solutionProvider === "huafenda";
  const isRongyuan = meta.solutionProvider === "rongyuan";
  const isXingshan = meta.solutionProvider.toLowerCase() === "xingshan";
  const isLightStrip = tab === "strip";
  const area = isLightStrip ? "Decorate1" : "Keyboard";
  const xingshanStripEquipment = useMemo(
    () => ({
      solutionRovider: meta.solutionProvider,
      keyboardType: meta.keyboardType,
      displayname: meta.displayname,
    }),
    [meta.solutionProvider, meta.keyboardType, meta.displayname]
  );
  const isXingshanRealtimeStrip =
    isXingshan &&
    isXingshanRealtimeStripKeyboardEquipment(xingshanStripEquipment);
  const useDk68FamilyStripPreview =
    isXingshan &&
    isXingshanDk68FamilyStripKeyboardEquipment(xingshanStripEquipment);
  const modeOptions = useMemo(() => {
    const base = getLightingModeOptions(meta.solutionProvider, area, t, {
      displayname: meta.displayname,
      keyboardType: meta.keyboardType,
      identifier: meta.deviceCatalog?.identifier,
    });
    const provider = meta.solutionProvider.toLowerCase();
    const iconProvider =
      provider === "huafenda"
        ? "huafenda"
        : provider === "beiying"
          ? "beiying"
          : provider === "rongyuan"
            ? "rongyuan"
            : provider === "zhwx"
              ? "zhwx"
              : provider === "sdcx"
                ? "sdcx"
                : "xingshan";
    return mapLightingOptionsWithIcons(
      base.map((item) => ({ label: item.label, value: item.value })),
      iconProvider,
      area
    );
  }, [
    meta.solutionProvider,
    meta.displayname,
    meta.keyboardType,
    meta.deviceCatalog,
    area,
    t,
  ]);
  const lightingParams = useMemo(
    () =>
      getLightingPreviewParams(configData, area, {
        solutionProvider: meta.solutionProvider,
      }),
    [configData, area, meta.solutionProvider]
  );
  const paletteColor = rgbToHex(lightingParams.primaryColor);
  const hfdEquipmentForPreview = useMemo(
    () => ({
      solutionRovider: meta.solutionProvider,
      displayname: meta.displayname,
      keyboardType: meta.keyboardType,
      identifier: meta.deviceCatalog?.identifier,
    }),
    [
      meta.solutionProvider,
      meta.displayname,
      meta.keyboardType,
      meta.deviceCatalog?.identifier,
    ]
  );
  const showHuafendaStripVisual =
    isHfd &&
    isLightStrip &&
    shouldShowHuafendaLightStripVisualPreview(hfdEquipmentForPreview);
  const showRongyuanStripVisual =
    isRongyuan &&
    isLightStrip &&
    shouldShowRongyuanLightStripVisualPreview({
      solutionProvider: meta.solutionProvider,
      keyboardType: meta.keyboardType,
    });
  const showStripVisualPreview =
    showHuafendaStripVisual || showRongyuanStripVisual;
  /** 华奋达 / 容园 X87S：灯带 Tab 展示虚拟灯带；背光 Tab 仍展示虚拟键盘 */
  const showKeyboardPreview = showStripVisualPreview
    ? false
    : isHfd || isRongyuan || tab === "backlight";
  const sliderRange = useMemo(
    () => getPreviewSliderRange(meta.solutionProvider, area),
    [meta.solutionProvider, area]
  );
  const brightness = resolvePreviewSliderValue(
    meta.solutionProvider,
    lightingParams.brightness,
    area
  );
  const speed = resolvePreviewSliderValue(
    meta.solutionProvider,
    lightingParams.speed,
    area
  );
  const hideHfdStripSpeed =
    isHfd && isLightStrip && shouldHideHuafendaStripSpeed(lightingParams.mode);
  const hideClassicX87LightStripSpeedAndBrightness =
    isHfd &&
    isLightStrip &&
    shouldHideHuafendaClassicX87LightStripSpeedAndBrightness({
      solutionRovider: meta.solutionProvider,
      displayname: meta.displayname,
    });
  const hideHfdStripPalette =
    isHfd &&
    isLightStrip &&
    shouldHideHuafendaStripPalette(lightingParams.mode);
  const showDirection = shouldShowLightingDirection(
    meta.solutionProvider,
    lightingParams.mode,
    area
  );
  const showStaticColor = shouldShowXingshanStaticColor(
    meta.solutionProvider,
    area
  );
  const decorateData = configData?.lighting?.decorate1;
  const stripPreviewCols = useMemo(
    () => resolveCommunityStripPreviewCols(meta, decorateData),
    [meta, decorateData]
  );
  const stripRoutineBase = useMemo(() => {
    if (!isXingshan || !isLightStrip) return null;
    return {
      open: lightingParams.lightOpen ? "Open" : "Close",
      mode: lightingParams.mode,
      luminance: lightingParams.brightness,
      speed: lightingParams.speed,
      direction: lightingParams.directionIsForward ? "Forward" : "Backward",
      selectStaticColor: lightingParams.selectStaticColor,
    };
  }, [
    isXingshan,
    isLightStrip,
    lightingParams.lightOpen,
    lightingParams.mode,
    lightingParams.brightness,
    lightingParams.speed,
    lightingParams.directionIsForward,
    lightingParams.selectStaticColor,
  ]);
  const stripRoutinePreviewEnabled =
    isXingshan &&
    isLightStrip &&
    !isXingshanRealtimeStrip &&
    shouldUseXingshanStripRoutinePreview(
      meta.solutionProvider,
      "Decorate1",
      stripRoutineBase
    );
  const stripRoutinePreview = useXingshanStripRoutinePreview(
    stripRoutinePreviewEnabled,
    stripRoutineBase,
    stripPreviewCols,
    useDk68FamilyStripPreview
  );
  const slicedDecorateData = useMemo(
    () => sliceCommunityStripLightingData(decorateData, stripPreviewCols),
    [decorateData, stripPreviewCols]
  );
  const stripLightingData = useMemo(() => {
    if (!isLightStrip) return slicedDecorateData ?? undefined;
    if (isHfd) {
      return (
        slicedDecorateData ??
        buildHuafendaStripPreviewFromBase(lightingParams, stripPreviewCols)
      );
    }
    if (isRongyuan) {
      return (
        slicedDecorateData ??
        buildRongyuanStripPreviewFromBase(lightingParams, stripPreviewCols)
      );
    }
    if (isXingshan) {
      if (isXingshanRealtimeStrip) return slicedDecorateData ?? undefined;
      return stripRoutinePreview ?? slicedDecorateData ?? undefined;
    }
    return slicedDecorateData ?? undefined;
  }, [
    isLightStrip,
    isHfd,
    isRongyuan,
    isXingshan,
    isXingshanRealtimeStrip,
    stripRoutinePreview,
    slicedDecorateData,
    lightingParams,
    stripPreviewCols,
  ]);
  const decorateRows = stripLightingData?.length ?? 1;
  const decorateCols = stripPreviewCols;
  const stripBrightness = isHfd
    ? Math.min(100, Math.max(0, brightness * 20))
    : lightingParams.brightness;

  const renderStaticColorPalette = () => (
    <div className="paletteConfig">
      {STATIC_EFFECT_PALETTE_COLORS.map((color, index) => (
        <div key={index} className="paletteConfig-item-wrap">
          <button
            type="button"
            className={`paletteConfig-item ${
              index === 0 ? "paletteConfig-item-rainbow" : ""
            } ${lightingParams.selectStaticColor === index ? "active" : ""}`}
            style={index === 0 ? undefined : { backgroundColor: color }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="community-kb-lighting-preview lighting">
      {lightingTabs.length > 1 ? (
        <div className="lighting-tab community-preview-interactive">
          <Tabs activeKey={tab} onChange={setTab}>
            <TabItem
              tabKey="backlight"
              label={t("keyboard.tab.keyboardBacklight")}
            />
            <TabItem tabKey="strip" label={t("keyboard.tab.lightStrip")} />
          </Tabs>
        </div>
      ) : null}

      <div className="lighting-keyboard">
        {showKeyboardPreview ? (
          <ViewportFit>
            <div className="lighting-keyboard-display">
              <Keyboard
                keyboardType={previewKeyboardType as never}
                layout={layout}
                styleMode="neutral"
                selectionMode="single"
                selectedKeys={[]}
                showLabels
                enablePhysicalKeyboard={false}
                disableViewportFit
              />
            </div>
          </ViewportFit>
        ) : (
          <div className="lighting-light-strip-display">
            <DecorativeLight
              config={{
                rows: decorateRows,
                cols: decorateCols,
                area: "Decorate1",
              }}
              lightingData={stripLightingData}
              brightness={stripBrightness}
              showBrightness
              ledSize={32}
              ledGap={0}
              fitWithinContainer
            />
          </div>
        )}
      </div>

      <div
        className={`lighting-main${isHfd ? " lighting-main-hfd" : ""}`}
        data-ui-workshop-slot={isLightStrip ? "lightStripBar" : "lightingBar"}
      >
        <div
          className={`lightingOperation${isLightStrip ? " lightingOperation--light-strip" : ""}`}
        >
          <div className="section-child-box">
            <Section
              title={t("keyboard.lighting.effectsTitle")}
              rightContent={
                <Switch
                  checked={lightingParams.lightOpen}
                  onChange={() => undefined}
                />
              }
            >
              <div className="formSection-box">
                <FormSection>
                  <ButtonGroup
                    className={`lighting-mode-button-group ${LIGHTING_MODE_BUTTON_GROUP_CLASS}`}
                    options={modeOptions}
                    value={
                      (isHfd || isRongyuan) && isLightStrip
                        ? lightingParams.mode
                        : lightingParams.lightOpen
                          ? lightingParams.mode
                          : ""
                    }
                    onChange={() => undefined}
                  />
                </FormSection>
              </div>
            </Section>
          </div>

          {!hideClassicX87LightStripSpeedAndBrightness ? (
            <div className="section-child-box section-child-speedAndBrightness-box">
              <Section title={t("keyboard.lighting.speedAndBrightness")}>
                <div className="formSection-box">
                  <div className="speed-brightness-row">
                    <FormSection
                      title={t("keyboard.lighting.lightBrightness")}
                      className="speed-brightness-item"
                    >
                      <Slider
                        min={sliderRange.min}
                        max={sliderRange.max}
                        value={brightness}
                        onChange={() => undefined}
                        showValue
                        unit={sliderRange.unit}
                        valueShowTopRight={Boolean(sliderRange.unit)}
                      />
                    </FormSection>
                    <FormSection
                      title={t("keyboard.lighting.lightSpeed")}
                      className="speed-brightness-item"
                    >
                      {hideHfdStripSpeed ? null : (
                        <Slider
                          min={sliderRange.min}
                          max={sliderRange.max}
                          value={speed}
                          onChange={() => undefined}
                          showValue
                          unit={sliderRange.unit}
                          valueShowTopRight={Boolean(sliderRange.unit)}
                        />
                      )}
                    </FormSection>
                  </div>

                  {showDirection ? (
                    <FormSection title={t("keyboard.lighting.directionLabel")}>
                      <div className="direction-control">
                        <Button
                          active={!lightingParams.directionIsForward}
                          onClick={() => undefined}
                        >
                          {t("keyboard.lighting.counterclockwise")}
                        </Button>
                        <Button
                          active={lightingParams.directionIsForward}
                          onClick={() => undefined}
                        >
                          {t("keyboard.lighting.clockwise")}
                        </Button>
                      </div>
                    </FormSection>
                  ) : null}

                  {showStaticColor ? (
                    <FormSection title={t("keyboard.lighting.effectColor")}>
                      {renderStaticColorPalette()}
                    </FormSection>
                  ) : null}
                </div>
              </Section>
            </div>
          ) : null}

          <div className="section-child-box">
            <Section
              title={t("keyboard.lighting.palette")}
              disabled={hideHfdStripPalette}
            >
              <div className="formSection-box">
                {isLightStrip && !isHfd ? (
                  renderStaticColorPalette()
                ) : (
                  <Palette
                    value={paletteColor}
                    onChange={() => undefined}
                    disabled={hideHfdStripPalette}
                    presetColors={[
                      "#ff0000",
                      "#ff8800",
                      "#ffff00",
                      "#00ff00",
                      "#00ffff",
                      "#0000ff",
                      "#8800ff",
                      "#ff00ff",
                    ]}
                  />
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
